import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Anchor, Globe, Handshake, TrendingUp, Sparkles } from "lucide-react";
import { EASE, Reveal, Eyebrow, Marquee, SectionHead } from "@/components/shared";
import Newsletter from "@/components/Newsletter";
import { API } from "@/lib/api";
import { IMAGES, PACKAGES } from "@/lib/content";
import { useConsult } from "@/App";

const HERO_LINES = ["Turning island", "visitors into", "loyal guests."];

const BENTO_SPANS = ["lg:col-span-4", "lg:col-span-8", "lg:col-span-8", "lg:col-span-4", "lg:col-span-6", "lg:col-span-6"];

export function FoundingOfferCard({ compact = false }) {
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    API.get("/founding-offer").then(({ data }) => setOffer(data)).catch(() => {});
  }, []);
  if (!offer) return null;
  return (
    <div className="mx-auto mt-10 max-w-xl rounded-2xl border border-gold/40 bg-gold/5 p-7" data-testid="founding-offer-card">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold-glow">Founding client offer</p>
      {offer.remaining > 0 ? (
        <>
          <p className="mt-3 font-serif text-xl leading-snug text-ivory" data-testid="founding-remaining">
            25% off your first project — {offer.remaining} of {offer.total} places remaining.
          </p>
          <p className="mt-3 text-sm font-light leading-relaxed text-slate-400">
            Priority delivery, lifetime care-plan pricing, and a founding place in our portfolio. When the places are
            gone, they're gone.
          </p>
        </>
      ) : (
        <p className="mt-3 font-serif text-xl leading-snug text-ivory" data-testid="founding-full">
          All {offer.total} founding places are taken. Join the waitlist and we'll honour the next opening at the
          founding rate.
        </p>
      )}
    </div>
  );
}

function Hero() {
  const ref = useRef(null);
  const { openConsult } = useConsult();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-36 pb-20 lg:pt-44 lg:pb-28" data-testid="hero-section">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,162,39,0.12),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(15,39,68,0.9),transparent_60%)]" />
      </div>
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Eyebrow>Technology Consultancy — Antigua &amp; Barbuda</Eyebrow>
          </motion.div>
          <h1 className="mt-6 font-serif text-4xl tracking-tight sm:text-5xl lg:text-[4.2rem]" style={{ lineHeight: 1.06 }} data-testid="hero-headline">
            {HERO_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-shimmer-gold italic" : "text-ivory"}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.25 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-7 max-w-xl text-base font-light leading-relaxed text-slate-300 sm:text-lg"
          >
            Elite Imperium Digital builds the websites, booking engines and quiet automation for businesses across
            the islands — on-island knowledge, delivered to international standards.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={openConsult}
              data-testid="hero-consultation-cta-btn"
              className="group flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light"
            >
              Book a free consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <Link
              to="/audit"
              data-testid="hero-audit-cta-btn"
              className="group flex items-center gap-2 rounded-full border border-gold/40 px-7 py-3.5 text-sm font-semibold text-gold-glow transition-colors duration-300 hover:border-gold hover:bg-gold/10"
            >
              Request a free audit
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
            data-testid="hero-stats"
          >
            {[["<3s", "page loads we build to"], ["Fixed price", "agreed before we start"], ["Plain English", "no jargon, ever"]].map(([v, l]) => (
              <div key={l}>
                <p className="font-mono text-xl font-medium text-gold-glow">{v}</p>
                <p className="mt-1 text-xs uppercase tracking-widest text-slate-400">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-3xl border border-gold/25" />
            <div className="relative overflow-hidden rounded-3xl">
              <motion.img
                src={IMAGES.harbor}
                alt="Golden hour over an Antigua harbour, yachts at anchor"
                style={{ y: imgY, scale: 1.15 }}
                className="h-[420px] w-full object-cover duotone lg:h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/80 via-transparent to-navy/20" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3, ease: EASE }}
              className="absolute -bottom-8 -left-4 rounded-2xl border border-gold/30 bg-navy-deep/90 p-5 backdrop-blur-xl sm:-left-10"
              data-testid="hero-metric-card"
            >
              <p className="font-serif text-2xl leading-snug text-gold-glow">Free consultation.<br />Fixed prices.</p>
              <p className="mt-2 text-xs uppercase tracking-widest text-slate-300">No obligation</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  const items = [
    { icon: Anchor, title: "On the ground in Antigua", text: "We live where you work. Site visits, face-to-face sessions, and a phone that gets answered — not a ticket queue in another timezone." },
    { icon: Globe, title: "International delivery standards", text: "An experienced regional delivery team builds to the same bar your guests expect from global brands. Fast, secure, beautiful." },
    { icon: Handshake, title: "One contact for everything", text: "Website, payments, automation, hosting, advice. One relationship, one invoice, zero finger-pointing between vendors." },
  ];
  return (
    <section className="py-24 lg:py-32" data-testid="why-us-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHead
          eyebrow="Why Elite Imperium"
          title="Local nuance. World-class execution. No compromises."
          lead="Part of the Elite Imperium Group, we exist so Caribbean businesses never have to choose between someone who understands the island and someone who can actually build it."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {items.map((item, i) => (
            <Reveal key={item.title} delay={i * 0.12}>
              <div className="gold-ring-card h-full p-8" data-testid={`why-card-${i}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
                  <item.icon className="h-5 w-5 text-gold-glow" />
                </div>
                <h3 className="mt-6 font-serif text-xl text-ivory">{item.title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-slate-400">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Bento() {
  return (
    <section className="py-24 lg:py-32" data-testid="services-bento-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHead
          eyebrow="What we deliver"
          title="Six ways we move your business forward"
          lead="Every engagement starts with outcomes — more bookings, less admin — then we choose the technology to get there."
        />
        <div className="mt-14 grid gap-5 lg:grid-cols-12">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.1} className={BENTO_SPANS[i]}>
              <Link
                to={`/services/${p.id}`}
                data-testid={`package-${p.id}-card`}
                className="gold-ring-card group flex h-full flex-col justify-between p-8 lg:p-10"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold/80">{p.tag}</span>
                    <ArrowUpRight className="h-5 w-5 text-gold/50 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold-glow" />
                  </div>
                  <h3 className="mt-4 font-serif text-2xl text-ivory">{p.name}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-slate-400">{p.desc}</p>
                </div>
                <ul className="mt-6 space-y-2">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-2 text-xs text-slate-300">
                      <span className="h-1 w-1 rotate-45 bg-gold" /> {pt}
                    </li>
                  ))}
                </ul>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function AuditBand() {
  return (
    <section className="relative overflow-hidden py-24 lg:py-28" data-testid="audit-band-section">
      <img src={IMAGES.yacht} alt="" className="absolute inset-0 h-full w-full object-cover duotone opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/85 to-navy/60" />
      <div className="relative mx-auto flex max-w-7xl flex-col items-start gap-10 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-10">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>Free Digital Audit</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-ivory sm:text-4xl" style={{ lineHeight: 1.15 }}>
              Find the revenue your website is quietly turning away.
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-5 text-base font-light leading-relaxed text-slate-300">
              A fifteen-page, plain-English analysis of your speed, search visibility and booking readiness — with a
              prioritised fix list. Free for businesses in Antigua &amp; Barbuda.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.25}>
          <Link
            to="/audit"
            data-testid="audit-band-cta-btn"
            className="group flex items-center gap-2 whitespace-nowrap rounded-full bg-gold px-8 py-4 text-sm font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light"
          >
            Claim my free audit
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function FirstProjects() {
  const { openConsult } = useConsult();
  return (
    <section className="py-24 lg:py-32" data-testid="first-projects-section">
      <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
        <Reveal><Eyebrow>Our first projects</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-serif text-3xl tracking-tight text-ivory sm:text-4xl" style={{ lineHeight: 1.15 }}>
            Case studies coming soon.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-5 text-base font-light leading-relaxed text-slate-300 sm:text-lg">
            We are taking our first founding clients now. Yours could be the first story we tell — real numbers,
            real outcomes, shared with your permission.
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <FoundingOfferCard />
        </Reveal>
        <Reveal delay={0.28}>
          <button
            onClick={openConsult}
            data-testid="founding-client-btn"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light"
          >
            Become a founding client
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}

function Dispatch() {
  return (
    <section className="py-24 lg:py-28" data-testid="newsletter-section">
      <div className="mx-auto max-w-3xl px-6 text-center lg:px-10">
        <Reveal>
          <Sparkles className="mx-auto h-6 w-6 text-gold" />
          <h2 className="mt-5 font-serif text-3xl tracking-tight text-ivory sm:text-4xl">The Caribbean Digital Dispatch</h2>
          <p className="mx-auto mt-4 max-w-xl text-base font-light leading-relaxed text-slate-400">
            One short email a month for island business owners — what is working in Caribbean digital, in plain English.
            No noise, unsubscribe anytime.
          </p>
          <div className="mx-auto mt-8 max-w-lg">
            <Newsletter />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <WhyUs />
      <Bento />
      <AuditBand />
      <FirstProjects />
      <Dispatch />
    </main>
  );
}

export function CTABand({ title, text }) {
  const { openConsult } = useConsult();
  return (
    <section className="border-t border-gold/10 py-24" data-testid="cta-band">
      <div className="mx-auto max-w-4xl px-6 text-center lg:px-10">
        <Reveal>
          <TrendingUp className="mx-auto h-7 w-7 text-gold" />
          <h2 className="mt-5 font-serif text-3xl tracking-tight text-ivory sm:text-4xl">{title}</h2>
          <p className="mx-auto mt-4 max-w-xl text-base font-light text-slate-400">{text}</p>
          <button
            onClick={openConsult}
            data-testid="cta-band-book-btn"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-navy-deep transition-colors duration-300 hover:bg-gold-light"
          >
            Book a free consultation
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}
